import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useAcademy } from '../AcademyContext';
import ExerciseTopBar from '../components/ExerciseTopBar';
import ExerciseReward from '../components/ExerciseReward';
import { IconArrowRight, IconArrowLeft } from '../components/Icons';

/**
 * Mini-Quiz — intermediate page between lesson and completion.
 *
 * Supports three question types:
 *  - multiple-choice  (pick one option)
 *  - text            (free text answer)
 *  - email           (subject + body)
 *
 * DUMMY: answers are kept in local state only. Once every question has an
 * answer, the quiz is marked done → the lesson counts as complete.
 */

const isAnswered = (question, answer) => {
  if (!answer) return false;
  if (question.type === 'multiple-choice') return answer.choice !== undefined && answer.choice !== null;
  if (question.type === 'text') return !!(answer.text && answer.text.trim());
  if (question.type === 'email') return !!(answer.subject && answer.subject.trim() && answer.body && answer.body.trim());
  return false;
};

const QuizPage = () => {
  const { topicId, courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { getTopic, getCourse, getLesson, markQuizDone, getCourseSteps } = useAcademy();
  const topic = getTopic(topicId);
  const course = getCourse(topicId, courseId);
  const lesson = getLesson(topicId, courseId, lessonId);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  // Destination to navigate to once the success reward has played.
  const [rewardTo, setRewardTo] = useState(null);

  // Each challenge starts at the top of the page, even if the previous step
  // was completed while scrolled down.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  if (!topic || !course || !lesson) return <Navigate to="/academy" replace />;
  if (!lesson.quiz) return <Navigate to={`/academy/${topicId}/${courseId}`} replace />;

  const questions = lesson.quiz.questions;
  const question = questions[step];
  const answer = answers[question.id] || {};
  const canContinue = isAnswered(question, answer);
  const isLastStep = step === questions.length - 1;

  const setAnswer = (patch) =>
    setAnswers((prev) => ({ ...prev, [question.id]: { ...prev[question.id], ...patch } }));

  const buildSubmission = () => ({
    type: 'quiz',
    answers: questions.map((q) => {
      const a = answers[q.id] || {};
      const base = { question: q.question, type: q.type };
      if (q.type === 'multiple-choice') {
        return {
          ...base,
          choiceIndex: a.choice ?? null,
          choiceText: q.options?.[a.choice] ?? '',
          correct: q.correctIndex != null ? a.choice === q.correctIndex : null,
        };
      }
      if (q.type === 'email') return { ...base, subject: a.subject || '', body: a.body || '' };
      return { ...base, text: a.text || '' };
    }),
  });

  // Route to the next step in the course path (mirrors PracticeLab).
  const stepRoute = (s) => {
    const base = `/academy/${topicId}/${courseId}/${s.lesson.id}`;
    if (s.type === 'practice') return `${base}/practice`;
    if (s.type === 'quiz') return `${base}/quiz`;
    return base;
  };

  const handleNext = () => {
    if (isLastStep) {
      if (rewardTo) return;
      markQuizDone(topicId, courseId, lessonId, buildSubmission());
      const steps = getCourseSteps(topicId, course);
      const idx = steps.findIndex((s) => s.type === 'quiz' && s.lesson.id === lessonId);
      const nextStep = idx >= 0 ? steps[idx + 1] : null;
      setRewardTo(nextStep ? stepRoute(nextStep) : `/academy/${topicId}/${courseId}`);
    } else {
      setStep((s) => s + 1);
    }
  };

  return (
    <main className="ac-page ac-quiz-page">
      <ExerciseTopBar
        kicker={course.title}
        title={lesson.title}
        closeTo={`/academy/${topicId}/${courseId}`}
      />

      <div className="ac-quiz-progress">
        {questions.map((q, i) => (
          <span
            key={q.id}
            className={`ac-quiz-dot ${i === step ? 'is-active' : ''} ${i < step ? 'is-done' : ''}`}
          />
        ))}
      </div>

      <div className="ac-quiz-card">
        <p className="ac-quiz-kicker">
          Question {step + 1} of {questions.length}
        </p>
        <h2 className="ac-quiz-question">{question.question}</h2>

        {question.type === 'multiple-choice' && (
          <div className="ac-choice-list">
            {question.options.map((opt, i) => (
              <button
                key={i}
                type="button"
                className={`ac-choice ${answer.choice === i ? 'is-selected' : ''}`}
                onClick={() => setAnswer({ choice: i })}
              >
                <span className="ac-choice-marker">{String.fromCharCode(65 + i)}</span>
                {opt}
              </button>
            ))}
          </div>
        )}

        {question.type === 'text' && (
          <div className="ac-field">
            <textarea
              className="ac-textarea"
              placeholder={question.placeholder || 'Your answer…'}
              value={answer.text || ''}
              onChange={(e) => setAnswer({ text: e.target.value })}
            />
          </div>
        )}

        {question.type === 'email' && (
          <>
            <div className="ac-field">
              <label className="ac-field-label">Subject</label>
              <input
                className="ac-input"
                placeholder={question.subjectPlaceholder || 'Subject line…'}
                value={answer.subject || ''}
                onChange={(e) => setAnswer({ subject: e.target.value })}
              />
            </div>
            <div className="ac-field">
              <label className="ac-field-label">Email body</label>
              <textarea
                className="ac-textarea"
                placeholder={question.bodyPlaceholder || 'Write your email…'}
                value={answer.body || ''}
                onChange={(e) => setAnswer({ body: e.target.value })}
              />
            </div>
          </>
        )}

        <div className="ac-quiz-foot">
          {step > 0 ? (
            <button type="button" className="ac-btn ac-btn-ghost" onClick={() => setStep((s) => s - 1)}>
              <IconArrowLeft size={15} /> Back
            </button>
          ) : (
            <span />
          )}
          <button type="button" className="ac-btn ac-btn-primary" onClick={handleNext} disabled={!canContinue}>
            {isLastStep ? 'Finish & complete' : 'Next'} <IconArrowRight size={15} />
          </button>
        </div>
      </div>

      {rewardTo && (
        <ExerciseReward
          onComplete={() => navigate(rewardTo, { state: { dir: 'forward' } })}
        />
      )}
    </main>
  );
};

export default QuizPage;
